import React, { ReactNode } from 'react'
import { Breadcrumbs, Button } from '@systemeio/components'
import { Save01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { UserRolesEnum } from 'shared/enums/user-roles-enum'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import ProfileMenuSkeleton from './profile-menu-skeleton'

function ProfileLayoutItem({
  show = true,
  href,
  label,
  ...options
}: React.LiHTMLAttributes<HTMLLIElement> & {
  show?: boolean
  href: string
  label: string
}): ReactNode {
  if (!show) return null

  return (
    <li {...options}>
      <Link
        href={href}
        data-active={globalThis.location.pathname === href}
        className="flex border-b border-b-text-secondary/10 p-5 text-text-primary hover:bg-bg-accent/10 focus:outline-hidden focus-visible:border-b-border-accent focus-visible:bg-bg-accent/10 data-[active=true]:border-l-4 data-[active=true]:border-l-border-accent data-[active=true]:bg-bg-accent/10 data-[active=true]:font-bold data-[active=true]:text-text-link"
      >
        {label}
      </Link>
    </li>
  )
}

function ProfileLayoutContent(): ReactNode {
  const { t } = useTranslation()
  const { user, loading: isLoading, showChangePasswordPage } = useUser()

  if (isLoading) {
    return <ProfileMenuSkeleton />
  }

  if (!isAuthorizedUser(user)) return null

  const hasCustomerRole = user.roles.includes(UserRolesEnum.Customer)

  return (
    <>
      <ProfileLayoutItem
        show={!user.workspaceOwner}
        key="user-settings"
        href="/profile/user-settings"
        label={t('profile.user_settings.profile.title')}
      />
      <ProfileLayoutItem
        show={!hasCustomerRole && !user.impersonator}
        key="account-settings"
        href="/profile/account-settings"
        label={t('profile.user_settings.account.title')}
      />
      <ProfileLayoutItem
        show={!user.workspaceOwner}
        key="settings-subscriptions"
        href="/profile/manage-subscriptions"
        label={t('profile.user_settings.manage_subscriptions.title')}
      />
      <ProfileLayoutItem
        key="notification-settings"
        href={
          hasCustomerRole && !isCustomDomain
            ? getDashboardNotificationSettingsLink(user)
            : '/profile/notification-settings'
        }
        label={t('profile.user_settings.manage_notifications.title')}
      />
      <ProfileLayoutItem
        show={showChangePasswordPage}
        key="change-passwords"
        href="/profile/change-password"
        label={t('profile.user_settings.change_password.title')}
      />
    </>
  )
}

export function ProfileLayout({
  isFetching,
  onSave,
  disabled,
  title,
  children,
}: {
  isFetching?: boolean
  onSave?: () => void
  disabled?: boolean
  title: string
  children: React.ReactNode
}): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()

  if (!user || !isAuthorizedUser(user)) {
    return <div />
  }

  const breadcrumbsItems = [{ label: t('user_settings.title') }, { label: title, current: true }]

  return (
    <>
      <h1 className="sr-only">{title}</h1>
      <div className="mb-3 flex h-10 flex-wrap justify-between gap-x-3">
        <Breadcrumbs items={breadcrumbsItems} />
        <div className="ml-auto flex gap-3 sm:ml-0">
          {onSave && (
            <Button
              variant="primary"
              data-testid="save-button"
              isLoading={isFetching}
              disabled={!user || disabled}
              onClick={onSave}
            >
              <Save01Icon className="size-5" />
              <span className="hidden font-bold sm:flex">{t('global.save')}</span>
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3 md:gap-5 lg:flex-row lg:gap-12">
        <div className="relative min-h-48.75 w-full lg:w-62.5">
          <ul className="static inset-0 flex h-fit w-full flex-col overflow-auto rounded-md bg-bg-primary text-base last:border-b-transparent lg:w-62.5">
            <ProfileLayoutContent />
          </ul>
        </div>
        {children}
      </div>
    </>
  )
}
